/** Jersey number bins (port of jersey_grid.py's NUMBER_PAGE, same
 *  address /labeling/jersey-grid/).
 *
 * A bin of crops that should all read the same predicted number:
 * green = confirm, click marks wrong. Submit walks the wrong ones
 * through the keypad fixer (two digits max, ⌫, ✓ records typed-or-
 * null, illegible), then POSTs {pred, confirms, corrections} — the
 * exact Jinja payload — and loads the next bin.
 */
import { useCallback, useEffect, useState } from 'react'

import { api } from '../../lib/api'
import './labeling.css'

type Task = { id: number; image: string }

const PAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '⌫', '0', '✓'] as const

export default function JerseyNumbers() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [pred, setPred] = useState('')
  const [wrong, setWrong] = useState<Record<number, boolean>>({})
  const [status, setStatus] = useState('loading...')
  // The fixer walk: queue of wrong tasks, current index, typed digits,
  // accumulated corrections.
  const [fixQueue, setFixQueue] = useState<Task[]>([])
  const [fixIdx, setFixIdx] = useState(0)
  const [typed, setTyped] = useState('')
  const [corrections, setCorrections] = useState<Record<number, string | null>>({})

  const loadBin = useCallback(() => {
    setStatus('loading...')
    setFixQueue([]); setFixIdx(0); setCorrections({})
    api<{ tasks?: Task[]; pred?: string; error?: string }>('/labeling/jersey-grid/bin')
      .then((j) => {
        if (j.error) { setStatus(j.error); return }
        setTasks(j.tasks ?? []); setPred(j.pred ?? ''); setWrong({}); setStatus('')
      })
      .catch((e: Error) => setStatus(e.message))
  }, [])

  useEffect(() => { loadBin() }, [loadBin])

  const finish = async (allCorrections: Record<number, string | null>) => {
    setStatus('submitting...')
    try {
      const j = await api<{ error?: string }>('/labeling/jersey-grid/submit', {
        method: 'POST',
        body: JSON.stringify({
          pred,
          confirms: tasks.filter((t) => !wrong[t.id]).map((t) => t.id),
          corrections: allCorrections,
        }),
      })
      if (j?.error) { setStatus(j.error); return }
      loadBin()
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'submit failed')
    }
  }

  const submitBin = () => {
    const queue = tasks.filter((t) => wrong[t.id])
    setCorrections({})
    if (queue.length) { setFixQueue(queue); setFixIdx(0); setTyped('') }
    else finish({})
  }

  const record = (val: string | null) => {
    const next = { ...corrections, [fixQueue[fixIdx]!.id]: val }
    setCorrections(next)
    setTyped('')
    if (fixIdx + 1 < fixQueue.length) setFixIdx(fixIdx + 1)
    else { setFixQueue([]); finish(next) }
  }

  const pad = (k: string) => {
    if (k === '⌫') setTyped((t) => t.slice(0, -1))
    else if (k === '✓') record(typed || null)
    else if (typed.length < 2) setTyped((t) => t + k)
  }

  const w = Object.values(wrong).filter(Boolean).length
  const stats = status || `${tasks.length} shown · ${tasks.length - w} confirm ${pred} · ${w} wrong`
  const fixing = fixQueue.length > 0

  return (
    <div className="lab-numbers">
      <div id="bar">
        {' '}<b>jersey numbers</b>{' '}
        <span id="binlabel">
          {pred && <>all of these should be <b className="pred">{pred}</b></>}
        </span>{' '}
        <span>green = confirm · click to mark <span className="wrongword">wrong</span></span>{' '}
        <button onClick={submitBin}>submit bin</button>{' '}
        <span id="stats">{stats}</span>
      </div>
      <div id="grid">
        {tasks.map((t) => (
          <div key={t.id} className={`cell ${wrong[t.id] ? 'wrong' : ''}`}
               onClick={() => setWrong((p) => ({ ...p, [t.id]: !p[t.id] }))}>
            <img loading="lazy" src={t.image} alt="" />
          </div>
        ))}
      </div>
      {fixing && (
        <div id="fix">
          <div><img id="fiximg" src={fixQueue[fixIdx]!.image} alt="" /></div>
          <div id="typed">{typed || '·'}</div>
          <div id="pad">
            {PAD_KEYS.map((k) => (
              <button key={k} onClick={() => pad(k)}>{k}</button>
            ))}
            <button className="illegible" onClick={() => record('__illegible__')}>
              illegible
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
